import { useSearchParams } from 'react-router';

const DEFAULT_CURSOR = '';
const DEFAULT_LIMIT = '10';
const DEFAULT_OFFSET = '0';
const DEFAULT_ORDER_BY = '';

const usePaginationSearchParams = (prefix: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get(`${prefix}Cursor`) ?? DEFAULT_CURSOR;
  const limit = searchParams.get(`${prefix}Limit`) ?? DEFAULT_LIMIT;
  const offset = searchParams.get(`${prefix}Offset`) ?? DEFAULT_OFFSET;
  const orderBy = searchParams.get(`${prefix}OrderBy`) ?? DEFAULT_ORDER_BY;

  const setPaginationParamsFactory = (
    key: string,
    {
      defaultValue,
      encode = false,
    }: { defaultValue?: string; encode?: boolean },
  ) => {
    return (value: Numberlish) => {
      setSearchParams(() => ({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        [`${prefix}${key}`]: encode
          ? encodeURIComponent(`${value ?? defaultValue}`)
          : `${value ?? defaultValue}`,
      }));
    };
  };

  const setCursor = setPaginationParamsFactory('Cursor', { defaultValue: '' });
  const setLimit = setPaginationParamsFactory('Limit', { defaultValue: '10' });
  const setOffset = setPaginationParamsFactory('Offset', { defaultValue: '0' });
  const setOrderBy = setPaginationParamsFactory('OrderBy', {
    defaultValue: '',
    encode: true,
  });
  const setPaginationParams = ({
    cursor,
    limit,
    offset,
    orderBy,
  }: {
    cursor?: string;
    limit?: number;
    offset?: number;
    orderBy?: string;
  }) => {
    setSearchParams({
      [`${prefix}Cursor`]: cursor ?? '',
      [`${prefix}Limit`]: limit ? `${limit}` : '',
      [`${prefix}Offset`]: offset ? `${offset}` : '',
      [`${prefix}OrderBy`]: orderBy ? encodeURIComponent(orderBy) : '',
    });
  };

  return {
    cursor,
    limit: +(limit ?? DEFAULT_LIMIT),
    offset: +(offset ?? DEFAULT_OFFSET),
    orderBy: decodeURIComponent(orderBy ?? DEFAULT_ORDER_BY),

    setCursor,
    setLimit,
    setOffset,
    setOrderBy,
    setPaginationParams,
  };
};

export default usePaginationSearchParams;
